import React, { useEffect, useState, useRef } from 'react';
import Preloader from '../../components/common/Preloader';
import { useParams, useLocation } from 'react-router-dom';
import { getEmployeeById, getPayrollStatus } from '../../services/adminService';
import logo from '../../assets/officiallogo.png';
import './SalarySlipPrint.css';


const SalarySlipPrint = () => {
    const { employeeId, month, year } = useParams();
    const location = useLocation();
    const [employee, setEmployee] = useState(null);
    const [payroll, setPayroll] = useState(null);
    const [loading, setLoading] = useState(true);
    const hasPrinted = useRef(false);

    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    useEffect(() => {
        const loadData = async () => {
            try {
                const emp = await getEmployeeById(employeeId);
                const payrolls = await getPayrollStatus(month, year);
                const record = payrolls.find(p => p.employee === employeeId);

                setEmployee(emp);
                setPayroll(record);
            } catch (err) {
                console.error("Failed to load slip data", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [employeeId, month, year]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const isPreview = queryParams.get('preview') === 'true';

        if (!loading && employee && payroll && !isPreview && !hasPrinted.current) {
            hasPrinted.current = true;
            setTimeout(() => {
                window.print();
            }, 500);
        }
    }, [loading, employee, payroll, location.search]);

    if (loading) return <Preloader />;
    if (!employee || !payroll) return <div>Data not found</div>;

    const convertNumberToWords = (amount) => {
        return "Rupees " + (amount || 0).toLocaleString() + " Only";
    };

    const formatDate = (dateString) => {
        if (!dateString) return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const slipNo = `CRZ/SLIP/${payroll.year}/${monthNames[payroll.month - 1].substring(0, 3).toUpperCase()}/${employee.employeeId || employee._id.substring(0, 5).toUpperCase()}`;
    const issueDate = formatDate(payroll.issueDate);
    const periodStartDate = `01 ${monthNames[payroll.month - 1]} ${payroll.year}`;
    const periodEndDate = `${new Date(payroll.year, payroll.month, 0).getDate()} ${monthNames[payroll.month - 1]} ${payroll.year}`;
    const shortPeriodStartDate = `01 ${monthNames[payroll.month - 1].substring(0, 3)} ${payroll.year}`;
    const shortPeriodEndDate = `${new Date(payroll.year, payroll.month, 0).getDate()} ${monthNames[payroll.month - 1].substring(0, 3)} ${payroll.year}`;

    return (
        <div className="salary-slip-container">

            {/* Top Header */}
            <div className="company-header">
                <div className="company-logo-wrapper">
                    <img src={logo} alt="Company Logo" className="company-logo-img" />
                </div>
                <div className="company-details-wrapper">
                    <h1 className="company-name-title">CRZ ACADEMIC REVIEW PRIVATE LIMITED</h1>
                    <div className="company-website"><a href="https://www.collegereviewz.com" target="_blank" rel="noreferrer">collegereviewz.com</a></div>
                    <div className="company-contact-info">
                        Contact No.: +91 9717987058 &nbsp;|&nbsp; Email: admin@collegereviewz.com &nbsp;|&nbsp; Website: www.collegereviewz.com
                    </div>
                </div>
            </div>

            <div className="header-divider"></div>

            <h2 className="slip-title">SALARY SLIP</h2>

            {/* Top Info Table */}
            <table className="info-table mb-10">
                <tbody>
                    <tr>
                        <td className="font-bold text-blue w-20">Salary Slip No.</td>
                        <td className="w-30">{slipNo}</td>
                        <td className="font-bold text-blue w-20">Date of Issue</td>
                        <td className="w-30">{issueDate}</td>
                    </tr>
                    <tr>
                        <td className="font-bold text-blue">Place</td>
                        <td>Kolkata</td>
                        <td className="font-bold text-blue">Salary Status</td>
                        <td className="font-bold text-green">PROCESSED</td>
                    </tr>
                </tbody>
            </table>

            <p className="certify-text">
                This is to certify that <strong>CRZ Academic Review Private Limited</strong> has prepared the salary slip mentioned below for Mr. {employee.name} for the salary period <strong>{periodStartDate} to {periodEndDate}</strong>.
            </p>

            <h3 className="section-title">Employee Details</h3>
            <table className="info-table mb-10">
                <tbody>
                    <tr>
                        <td className="font-bold text-blue w-20">Employee Name</td>
                        <td className="w-30 font-bold">{employee.name}</td>
                        <td className="font-bold text-blue w-20">Employee ID</td>
                        <td className="w-30 font-bold">{employee.employeeId || employee._id.substring(0, 5).toUpperCase()}</td>
                    </tr>
                    <tr>
                        <td className="font-bold text-blue">Designation</td>
                        <td className="font-bold">{employee.designation || 'Software Engineer'}</td>
                        <td className="font-bold text-blue">Department</td>
                        <td>IT / Software</td>
                    </tr>
                    <tr>
                        <td className="font-bold text-blue">Salary Month</td>
                        <td>{monthNames[payroll.month - 1]} {payroll.year}</td>
                        <td className="font-bold text-blue">Salary Period</td>
                        <td className="font-bold">{shortPeriodStartDate} - {shortPeriodEndDate}</td>
                    </tr>
                    <tr>
                        <td className="font-bold text-blue">Date of Joining</td>
                        <td>{employee.dateOfJoining ? new Date(employee.dateOfJoining).toLocaleDateString('en-GB').replace(/\//g, '.') : 'N/A'}</td>
                        <td className="font-bold text-blue">Employment Type</td>
                        <td>{employee.employeeType || 'Full Time'}</td>
                    </tr>
                    <tr>
                        <td className="font-bold text-blue">Bank Name</td>
                        <td>{employee.bankName || 'N/A'}</td>
                        <td className="font-bold text-blue">Account No</td>
                        <td>{employee.accountNo || 'N/A'}</td>
                    </tr>
                    <tr>
                        <td className="font-bold text-blue">Pan Number</td>
                        <td>{employee.panNumber || 'N/A'}</td>
                        <td className="font-bold text-blue">UAN Number</td>
                        <td>{employee.uanNumber || 'N/A'}</td>
                    </tr>
                </tbody>
            </table>

            <h3 className="section-title">Salary Components / Payment Details</h3>
            <table className="components-table mb-10">
                <thead>
                    <tr>
                        <th>Particulars</th>
                        <th>Amount (INR)</th>
                        <th>Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Fixed Monthly Salary</td>
                        <td className="text-center">{Math.round(payroll.basicSalary).toLocaleString('en-IN')}</td>
                        <td>Reference monthly salary</td>
                    </tr>
                    <tr>
                        <td>Additional Allowance / Adjustment</td>
                        <td className="text-center">{Math.round(payroll.allowances || 0).toLocaleString('en-IN')}</td>
                        <td>Additional amount recorded</td>
                    </tr>
                    <tr>
                        <td>Earned Salary for {monthNames[payroll.month - 1]} {payroll.year}</td>
                        <td className="font-bold text-center">{Math.round(payroll.basicSalary + (payroll.allowances || 0)).toLocaleString('en-IN')}</td>
                        <td>As per company salary record</td>
                    </tr>
                    <tr>
                        <td>Leave Deductions</td>
                        <td className="text-center text-red" style={{ color: '#ef4444' }}>- {Math.round(payroll.deductions || 0).toLocaleString('en-IN')}</td>
                        <td>Deduction for leaves, half-days & Others</td>
                    </tr>
                    <tr className="bg-light-green">
                        <td className="font-bold">Net Salary Paid / Payable</td>
                        <td className="font-bold text-center text-green">{Math.round(payroll.netSalary).toLocaleString('en-IN')}</td>
                        <td className="font-bold">Salary amount for {monthNames[payroll.month - 1]} {payroll.year}</td>
                    </tr>
                    <tr>
                        <td>Amount in Words</td>
                        <td className="text-center">{convertNumberToWords(payroll.netSalary)}</td>
                        <td>Total net salary converted to words</td>
                    </tr>
                    <tr>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Payment Mode / Transaction ID</td>
                        <td className="text-center" style={{ wordBreak: 'break-all', fontWeight: 'bold' }}>{payroll.transactionId || 'Not Provided'}</td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Payment Date: {payroll.paymentDate ? new Date(payroll.paymentDate).toLocaleString('en-US', { hour12: true, year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not Provided'}</td>
                    </tr>
                </tbody>
            </table>

            <p className="declaration-text">
                <strong>Declaration:</strong> The above salary slip is prepared as per the company salary record for the month of {monthNames[payroll.month - 1]} {payroll.year}. This document is issued for employee salary confirmation and internal/company records.
            </p>

            <div className="acknowledgement-box">
                Employee Acknowledgement: I, {employee.name}, acknowledge the salary amount of INR {payroll.netSalary.toLocaleString()} ({convertNumberToWords(payroll.netSalary)}) towards my salary for the month of {monthNames[payroll.month - 1]} {payroll.year}.
            </div>

            <div style={{ marginTop: 'auto' }}>
                <div className="signatures-section">
                    <div className="sign-block left-sign">
                        <div className="sign-title text-blue">Authorized Signatory (Optional)</div>
                        <div className="sign-text">For CRZ Academic Review Private Limited</div>
                        <div className="sign-text">Company Stamp (Optional)</div>
                    </div>
                    <div className="sign-block right-sign">
                        <div className="sign-title text-blue">Employee Signature (Optional)</div>
                        <div className="sign-text">{employee.name}</div>
                        <div className="sign-text date-line">Date: ________________________</div>
                    </div>
                </div>

                <div className="footer-note">
                    This is a system generated document. Stamp and signature are optional.
                </div>

                <div className="footer-bottom-line"></div>
                <div className="footer-contact-info">
                    Office: 217, 2nd Floor, Diamond Arcade, 68 Jessore Road, on Shyam Nagar Road, Opp. GD Goenka School, Kolkata -700055<br />
                    Email: Admin@collegereviewz.com &nbsp;|&nbsp; Phone: +91 9717987058 &nbsp;|&nbsp; Web: www.collegereviewz.com<br />
                    CIN: U85500WR2026PTC292539 &nbsp;|&nbsp; PAN: AANCC6135G &nbsp;|&nbsp; TAN: CALC16240A
                </div>
            </div>
        </div>
    );
};

export default SalarySlipPrint;
